// src/components/PushDeepLinkHandler.js
// ✅ FIX DEEP-LINKING P0: Handler pour ouvrir une conversation depuis une notification push
// ✅ PHASE 3b: Marquer automatiquement la notification comme lue dans l'app
// ✅ PHASE 3c: Navigation vers targetUrl pour toutes les notifications (event_nearby, post_*, etc.)
//
// Ce composant:
// 1. Écoute les messages du Service Worker (app déjà ouverte)
// 2. Lit le query param ?pushConversationId et ?pushNotifId au boot (cold start iOS PWA)
// 3. Émet l'événement openFloatingConversation pour ouvrir la conversation
// 4. Marque automatiquement la notification comme lue via l'API
// 5. Navigue vers la page cible pour les autres types de notifications

import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { markNotificationReadOnServer } from '../store/notifications/notificationsActions'

/**
 * Hook/Composant pour gérer le deep-linking des notifications push
 * @param {boolean} isReady - true quand l'app est prête à ouvrir des conversations
 */
const PushDeepLinkHandler = ({ isReady }) => {
  const hasProcessedIntent = useRef(false)
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  useEffect(() => {
    if (!isReady) return

    // Fonction pour marquer une notification comme lue
    const markNotificationAsRead = (notifId) => {
      if (notifId) {
        console.log('[PushDeepLink] Marquage notification comme lue:', notifId)
        dispatch(markNotificationReadOnServer(Number(notifId)))
      }
    }

    // 1. Écouter les messages du Service Worker (app déjà ouverte)
    const handleServiceWorkerMessage = (event) => {
      if (!event.data) return

      // Cas 1: Notification de type message (ouvre la conversation)
      if (event.data.type === 'OPEN_CONVERSATION') {
        const conversationId = event.data.conversationId
        const notifId = event.data.notifId
        console.log('[PushDeepLink] Message SW reçu, conversation:', conversationId, 'notifId:', notifId)

        // Marquer la notification comme lue
        markNotificationAsRead(notifId)

        // Délai 300-600ms pour s'assurer que le FloatingMessagingManager est prêt
        setTimeout(() => {
          console.log('[PushDeepLink] Dispatch openFloatingConversation:', conversationId)
          window.dispatchEvent(
            new CustomEvent('openFloatingConversation', {
              detail: { conversationId: Number(conversationId) },
            }),
          )
        }, 400)
      }

      // Cas 2: Autres types de notifications (event_nearby, post_*, profile_view, etc.)
      if (event.data.type === 'PUSH_NOTIFICATION_CLICKED') {
        const notifId = event.data.notifId
        const targetUrl = event.data.targetUrl
        const notificationType = event.data.notificationType
        console.log('[PushDeepLink] Notification cliquée, notifId:', notifId, 'targetUrl:', targetUrl)

        // Marquer la notification comme lue
        markNotificationAsRead(notifId)

        // Cas profile_view: ouvrir la modale du visiteur au lieu de naviguer
        if (notificationType === 'profile_view' && event.data.viewerId) {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('openProfileViewModal', {
                detail: { viewerId: Number(event.data.viewerId) },
              }),
            )
          }, 300)
        } else if (targetUrl && !window.location.pathname.includes(targetUrl)) {
          // Naviguer vers la page cible si différente de l'URL actuelle
          console.log('[PushDeepLink] Navigation vers:', targetUrl)
          setTimeout(() => {
            navigate(targetUrl)
          }, 100)
        }
      }
    }

    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage)

    // 2. Lire les query params au boot (cold start iOS PWA) - une seule fois
    const processQueryParam = () => {
      if (hasProcessedIntent.current) return
      hasProcessedIntent.current = true

      const params = new URLSearchParams(location.search)
      const pushConversationId = params.get('pushConversationId')
      const pushNotifId = params.get('pushNotifId')

      // Marquer la notification comme lue si présente (même sans conversationId)
      if (pushNotifId) {
        console.log('[PushDeepLink] Query param pushNotifId trouvé:', pushNotifId)
        // Délai pour attendre que l'auth soit prête
        setTimeout(() => {
          markNotificationAsRead(pushNotifId)
        }, 1000)
      }

      if (pushConversationId) {
        console.log('[PushDeepLink] Query param pushConversationId trouvé:', pushConversationId)

        // Nettoyer l'URL immédiatement via history.replaceState (plus direct que navigate)
        const cleanUrl = window.location.origin + location.pathname
        window.history.replaceState(null, '', cleanUrl)
        console.log('[PushDeepLink] URL nettoyée:', cleanUrl)

        // Délai pour cold start (l'app doit charger complètement)
        setTimeout(() => {
          console.log('[PushDeepLink] Dispatch openFloatingConversation (cold start):', pushConversationId)
          window.dispatchEvent(
            new CustomEvent('openFloatingConversation', {
              detail: { conversationId: Number(pushConversationId) },
            }),
          )
        }, 1500)
      } else if (params.get('pushViewerId')) {
        // Cold start profile_view: ouvrir la modale du visiteur
        const pushViewerId = params.get('pushViewerId')
        const cleanUrl = window.location.origin + location.pathname
        window.history.replaceState(null, '', cleanUrl)

        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent('openProfileViewModal', {
              detail: { viewerId: Number(pushViewerId) },
            }),
          )
        }, 1500)
      } else if (pushNotifId) {
        // Si on a un notifId mais pas de conversationId, nettoyer quand même l'URL
        const cleanUrl = window.location.origin + location.pathname
        window.history.replaceState(null, '', cleanUrl)
      }
    }

    processQueryParam()

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage)
    }
  }, [isReady, location.search, location.pathname, dispatch, navigate])

  // Ce composant ne rend rien
  return null
}

export default PushDeepLinkHandler
